/**
 * Chat Members Database Helpers
 *
 * Fetch chats that are available through chat membership rows.
 *
 * Primary responsibilities:
 * - Load group chats shared with a user
 * - Load personal member chats for a workspace
 *
 * Dependencies:
 * - Supabase `chat_members` -> `chats` relationship
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { getChatReadStateSequenceNumbers } from '@/db/chat-read-states'
import { createServiceClient } from '@/lib/supabase/service-client'
import type { Database, Tables } from '@/supabase/types'

type ChatRow = Tables<'chats'>
type MessageActivityRow = Pick<
  Tables<'messages'>,
  'chat_id' | 'id' | 'sequence_number' | 'user_id'
>
export type ChatUnreadActivityFields = {
  unread_activity?: boolean
  unread_activity_message_id?: string | null
  unread_activity_sequence_number?: number | null
}
export type ChatWithUnreadActivity = ChatRow & ChatUnreadActivityFields
const ACTIVE_CHAT_MEMBER_STATUSES = ['accepted', 'active'] as const
type AuthenticatedFetch = typeof fetch

type ChatMemberJoinRow = {
  chats: ChatRow | null
}

// --------------

function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()

  return require('@/lib/supabase/browser-client')
    .supabase as SupabaseClient<Database>
}

const getAuthenticatedFetch = (): AuthenticatedFetch => {
  const mod = require('@/lib/auth/authenticated-fetch') as {
    authenticatedFetch: AuthenticatedFetch
  }

  return mod.authenticatedFetch
}

// --------------

function getChatTimestamp(value?: string | null) {
  const timestamp = value ? Date.parse(value) : Number.NEGATIVE_INFINITY
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY
}

// --------------

function getMessageSequenceNumber(value: unknown) {
  const sequenceNumber = Number(value)

  return Number.isFinite(sequenceNumber) ? sequenceNumber : null
}

// --------------

async function getLatestMessageForChat(
  chatId: string,
  client: SupabaseClient<Database>
) {
  const { data, error } = await client
    .from('messages')
    .select('chat_id, id, sequence_number, user_id')
    .eq('chat_id', chatId)
    .order('sequence_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return data as MessageActivityRow | null
}

// --------------

type LatestMessagesRpcClient = {
  rpc: (
    fn: 'latest_messages_for_chats',
    args: { p_chat_ids: string[] }
  ) => Promise<{ data: MessageActivityRow[] | null; error: { message: string } | null }>
}

const LATEST_MESSAGE_FALLBACK_CONCURRENCY = 8

async function getLatestMessagesByChatId(
  chatIds: string[],
  client: SupabaseClient<Database>
) {
  const byChatId = new Map<string, MessageActivityRow>()

  if (chatIds.length === 0) {
    return byChatId
  }

  // One DISTINCT ON query via RPC (migration 20261021030000). The old
  // fan-out fired one messages query per group chat — 200+ parallel
  // PostgREST calls for heavy users — and any single failure threw,
  // 500'ing the whole accessible-group-chats route on every poll.
  try {
    const { data, error } = await (client as unknown as LatestMessagesRpcClient).rpc(
      'latest_messages_for_chats',
      { p_chat_ids: chatIds }
    )

    if (error) {
      throw new Error(error.message)
    }

    for (const row of data ?? []) {
      byChatId.set(row.chat_id, row)
    }

    return byChatId
  } catch {
    // RPC unavailable (older DB / stale schema cache): bounded fan-out below.
  }

  for (
    let offset = 0;
    offset < chatIds.length;
    offset += LATEST_MESSAGE_FALLBACK_CONCURRENCY
  ) {
    const batch = chatIds.slice(offset, offset + LATEST_MESSAGE_FALLBACK_CONCURRENCY)

    await Promise.all(
      batch.map(async (chatId) => {
        try {
          const message = await getLatestMessageForChat(chatId, client)
          if (message) {
            byChatId.set(chatId, message)
          }
        } catch {
          // Tolerated: this chat just loses its unread badge this load.
        }
      })
    )
  }

  return byChatId
}

// --------------

async function withUnreadActivity(
  chats: ChatRow[],
  userId: string,
  client: SupabaseClient<Database>
): Promise<ChatWithUnreadActivity[]> {
  const groupChatIds = chats
    .filter((chat) => Boolean(chat.is_group_chat))
    .map((chat) => chat.id)

  if (groupChatIds.length === 0) {
    return chats
  }

  const [latestMessagesByChatId, readSequenceNumbersByChatId] = await Promise.all([
    getLatestMessagesByChatId(groupChatIds, client),
    getChatReadStateSequenceNumbers({
      chatIds: groupChatIds,
      userId,
      client
    })
  ])

  return chats.map((chat) => {
    if (!chat.is_group_chat) {
      return chat
    }

    const latestMessage = latestMessagesByChatId.get(chat.id) ?? null
    const latestSequenceNumber = getMessageSequenceNumber(latestMessage?.sequence_number)
    const lastReadSequenceNumber = readSequenceNumbersByChatId.get(chat.id)
    const hasUnreadActivity =
      latestSequenceNumber !== null &&
      latestMessage?.user_id !== userId &&
      (lastReadSequenceNumber === undefined ||
        lastReadSequenceNumber < latestSequenceNumber)

    return {
      ...chat,
      unread_activity: hasUnreadActivity,
      unread_activity_message_id: latestMessage?.id ?? null,
      unread_activity_sequence_number: latestSequenceNumber
    }
  })
}

// --------------

async function readBrowserJsonError(response: Response, fallbackMessage: string) {
  const responseText = await response.text().catch(() => '')

  if (!responseText) {
    return fallbackMessage
  }

  try {
    const payload = JSON.parse(responseText) as { error?: string }
    return payload.error || fallbackMessage
  } catch {
    return responseText
  }
}

// --------------

async function readBrowserGroupChats() {
  const response = await getAuthenticatedFetch()('/api/chat/groups/accessible', {
    cache: 'no-store',
    credentials: 'include'
  })

  if (!response.ok) {
    throw new Error(await readBrowserJsonError(response, 'Failed to load group chats'))
  }

  return (await response.json()) as ChatRow[]
}

async function readBrowserWorkspacePersonalChats(workspaceId: string) {
  const response = await getAuthenticatedFetch()(
    `/api/chat/workspaces/${encodeURIComponent(workspaceId)}/chats?all=true&limit=200`,
    {
      cache: 'no-store',
      credentials: 'include'
    }
  )

  if (!response.ok) {
    throw new Error(await readBrowserJsonError(response, 'Failed to load personal chats'))
  }

  const payload = (await response.json()) as { chats?: ChatRow[] }
  return (payload.chats ?? []).filter((chat) => !chat.is_group_chat)
}

// --------------

function mapJoinedChats(rows: ChatMemberJoinRow[] | null) {
  const chats = (rows ?? []).flatMap((row) => (row.chats ? [row.chats] : []))

  return Array.from(new Map(chats.map((chat) => [chat.id, chat])).values()).sort(
    (left, right) => {
      const leftTimestamp = getChatTimestamp(left.updated_at ?? left.created_at)
      const rightTimestamp = getChatTimestamp(right.updated_at ?? right.created_at)

      return rightTimestamp - leftTimestamp
    }
  )
}

// --------------

export async function getUserGroupChats(
  userId: string,
  _workspaceId: string,
  client?: SupabaseClient<Database>
) {
  if (!client && typeof window !== 'undefined') {
    return readBrowserGroupChats()
  }

  const db = getDefaultClient(client)
  const { data, error } = await db
    .from('chat_members')
    .select('chats!inner(*)')
    .eq('user_id', userId)
    .in('status', [...ACTIVE_CHAT_MEMBER_STATUSES])

  if (error) {
    throw new Error(error.message)
  }

  const groupChats = mapJoinedChats(data as ChatMemberJoinRow[]).filter((chat) =>
    Boolean(chat.is_group_chat)
  )

  try {
    return await withUnreadActivity(groupChats, userId, db)
  } catch {
    // Unread badges are decoration — never fail the sidebar load for them.
    return groupChats
  }
}

// --------------

export async function getUserPersonalChats(
  userId: string,
  workspaceId: string,
  client?: SupabaseClient<Database>
) {
  if (!client && typeof window !== 'undefined') {
    return readBrowserWorkspacePersonalChats(workspaceId)
  }

  const db = getDefaultClient(client)
  const { data, error } = await db
    .from('chat_members')
    .select('chats!inner(*)')
    .eq('user_id', userId)
    .in('status', [...ACTIVE_CHAT_MEMBER_STATUSES])

  if (error) {
    throw new Error(error.message)
  }

  return mapJoinedChats(data as ChatMemberJoinRow[]).filter(
    (chat) => chat.workspace_id === workspaceId && !chat.is_group_chat
  )
}
